import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Button, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { hideNavbarAtom, navbarOpenAtom, onTestPageAtom } from '~/atoms/atoms';
import { erroredOperationAtom } from '~/atoms/no-code-atoms';
import TestDraftView from '~/components/testDraft/TestDraftView';
import { useQueryParam } from '~/hooks/nav/useQueryParam';
import { useResetRecorderAtoms } from '~/hooks/vm/useResetRecorder';
import { useProjectId } from '~/hooks/context/useProjectId';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';
import { useCreateAndNavToTest } from '~/hooks/api/useCreateAndNavToTest';

// We split the out from the TestDraftView component to ensure that the draft is loading.
export const getTestDraftPath = ({ projectId, testDraftId }: { projectId: string; testDraftId: string }) =>
  `/project/${projectId}/testDraft/${testDraftId}`;

export default function TestDraft() {
  const router = useRouter();
  const projectId = useProjectId();
  const testDraftId = queryParamToString(router.query.testDraftId);

  const hideNavbar = useAtomValue(hideNavbarAtom);
  const setNavbarOpen = useSetAtom(navbarOpenAtom);
  const setOnTestPage = useSetAtom(onTestPageAtom);
  const [fromTest] = useQueryParam('fromTest');
  const setErroredOperation = useSetAtom(erroredOperationAtom);
  const createTestHandler = useCreateAndNavToTest({});

  const { data: testDraft, isLoading: draftIsLoading } = api.testContent.get.useQuery(
    { id: testDraftId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testDraftId },
  );
  const resetRecorderAtoms = useResetRecorderAtoms(testDraft?.id ?? '');

  useEffect(() => {
    setNavbarOpen(false);
    setOnTestPage(true);

    return () => {
      setNavbarOpen(!hideNavbar);
      setOnTestPage(false);
    };
  }, [setNavbarOpen, setOnTestPage, hideNavbar]);

  useEffect(() => {
    return () => {
      if (testDraft?.test?.id) {
        resetRecorderAtoms().catch(() => undefined);
      }
    };
  }, [testDraft?.test?.id]);

  useEffect(() => {
    setErroredOperation(undefined);
  }, [setErroredOperation]);

  return !draftIsLoading && !testDraft ?
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Title
            c="dimmed"
            className="text-8xl"
          >
            404
          </Title>
          <Title
            order={2}
            c="dimmed"
          >
            Test Draft Not Found
          </Title>
          <Text
            c="dimmed"
            ta="center"
          >
            The test draft you're looking for doesn't exist or may have been deleted.
          </Text>
          <div className="flex flex-col gap-3">
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="light"
              onClick={() => router.push(`/project/${projectId}/testDraft`).catch(() => undefined)}
            >
              Back to Test Drafts
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={createTestHandler}
            >
              Create New Test Draft
            </Button>
          </div>
        </div>
      </div>
    : <TestDraftView
        testDraftId={testDraftId}
        fromTest={fromTest !== undefined ? fromTest === 'true' : undefined}
      />;
}
